import { Badge, HipaaBadge } from "../../components/ui/Badge";
import { formatClockRange } from "../../lib/dates";
import { DropNote } from "./BlockCard";
import { EntryClose } from "./CloseTicket";
import type { LoggedEntryView } from "./model";
import { TicketStateRow } from "./TicketStateRow";
import type { DropZone } from "./useDragToMerge";

/**
 * Time the tech logged directly in Autotask (section 9.6). Its own style, a
 * dashed border: it isn't a block, so it can't be selected or logged again.
 *
 * It sits in Logged - Open or Closed by its ticket's state, as a logged block
 * does, and says so with the same chip; an open ticket's card has a small
 * **Close** (the owner's decision 3, 2026-10-21; ADR 0183). It flashes the
 * logged green when its ticket has just closed.
 *
 * A block dragged over it can be dropped on its ticket while that's open, to
 * log the block there as its own time entry (v0.9.0's P4): it says "Log 25m
 * to ticket T… (open)", or why not.
 */
export function LoggedEntryCard({
  entry,
  timeZone,
  now,
  justClosed = false,
  drop,
}: {
  entry: LoggedEntryView;
  timeZone: string;
  /** The time now, for its ticket's age ("open 2 d"); the time it's drawn by default. */
  now?: Date;
  /** Its ticket closed since this page last saw it (v0.9.0's P2b). */
  justClosed?: boolean;
  /** Dropping a block on its ticket (v0.9.0's P4); none without a ticket. */
  drop?: DropZone | undefined;
}) {
  return (
    <li
      className="relative motion-safe:animate-block-in"
      data-just-closed={justClosed || undefined}
      onDragEnter={drop?.onDragEnter}
      onDragOver={drop?.onDragOver}
      onDragLeave={drop?.onDragLeave}
      onDrop={drop?.onDrop}
    >
      <article
        aria-label={`${entry.title}, logged in Autotask`}
        className={
          "flex flex-col gap-3.5 rounded-card border border-dashed border-line bg-panel px-4 py-3.5 text-text" +
          (justClosed ? " motion-safe:animate-logged-flash" : "")
        }
      >
        <span className="flex items-center justify-between gap-2">
          <Badge tone="logged">AUTOTASK</Badge>
          <span className="font-mono text-xs text-muted">
            {entry.endedAt === null ? "No times in Autotask" : formatClockRange(entry.startedAt, entry.endedAt, timeZone)}
          </span>
        </span>
        <span className="flex items-end justify-between gap-2.5">
          <span className="flex min-w-0 flex-col gap-[3px]">
            <span className="truncate text-base font-[650] tracking-[-0.015em]">{entry.title}</span>
            <span className="text-[12.5px] text-muted">{entry.meta}</span>
            {entry.covers > 0 ? (
              <span className="text-[12.5px] text-muted">
                {entry.covers === 1 ? "Covers 1 block from here" : `Covers ${String(entry.covers)} blocks from here`}
              </span>
            ) : null}
          </span>
          <span className="text-[30px] leading-none font-semibold tracking-[-0.04em]">{entry.duration}</span>
        </span>
        {entry.ticket === null ? null : (
          <TicketStateRow ticket={entry.ticket} now={now ?? new Date()} timeZone={timeZone} />
        )}
        {entry.ticket === null ? null : <EntryClose ticket={entry.ticket} />}
        {entry.isHipaa ? <HipaaBadge /> : null}
      </article>
      {drop?.hint ? <DropNote hint={drop.hint} /> : null}
    </li>
  );
}
